<!--
Event manager

Uses an API from the event manager plugin to get event or instance availability & eligibility

Works in three steps:
  1) Go to the vue store and request the availability data
    - this done in the vue store so if another component has already requested the data, we dont have to make another api call
  2) Use that data to calculate availability & eligibility, and set them as strings
  3) When both functions are done, we can run any other function
    - in boiler this sets the button text/state/url
    - this is set in a watch function
-->
<template>
	<div>
		<slot
			:data-ready="dataReady"
			:on-sale="onSale"
			:availability="availability"
			:eligibility="eligibility"
			:button-data="buttonData"
			:current-on-sale-schedule="currentOnSaleSchedule"
		>
		</slot>
	</div>
</template>
<script>
import Axios from "axios";
import { setupCache } from "axios-cache-interceptor";
import { RequestManager } from "@jaseeey/request-manager";
import { CronJob } from "cron";

const instance = Axios.create();
const axios = setupCache(instance);

export default {
	name: "event-manager",

	data: function () {
		return {
			loading: true,
			availability: "",
			availabilityChecked: false,
			showAvailabilityInfo: true,
			eligibility: "",
			eligibilityChecked: false,
			error: "",
			currentDateTime: null,
			currentOnSaleSchedule: null,
			apiEventData: null,
			onSale: false,
		};
	},
	computed: {
		dataReady() {
			return this.availabilityChecked && this.eligibilityChecked;
		},
	},
	props: {
		type: {
			type: String,
		},
		instanceData: {
			type: Object,
		},
		eventData: {
			type: Object,
		},
		importData: {
			type: Object,
		},
		availabilityData: {
			type: Object,
		},
		buttonData: {
			type: Object,
		},
		config: {
			type: Object,
			default: () => ({
				getAvailability: true,
				getEligibility: true,
				getLockedSeats: false,
				runCron: true,
			}),
		},
	},

	async mounted() {
		const self = this;

		if (!this.instanceData.stopSellingDate) {
			this.onSale = true;
		}

		try {
			const response = await RequestManager.call(
				axios,
				"GET",
				"/datetime.php",
			);
			self.currentDateTime = Date.parse(response.data);

			if (self.instanceData.stopSellingDate) {
				let stopSellingDate = Date.parse(this.instanceData.stopSellingDate);

				self.onSale =
					!stopSellingDate || stopSellingDate > self.currentDateTime;
			}

			try {
				const response = await RequestManager.call(
					axios,
					"GET",
					"/actions/event-manager/availability/event?id=" +
					self.eventData.eventId,
					null,
					{
						cache: {
							ttl: 1000 * 60, // 1 minute
						},
					},
				);
				self.apiEventData = response.data;

				self.setAvailability();
				self.setEligibility();
			} catch (err) {
				console.log(err);
			}
		} catch (err) {
			console.log(err);
		}
	},

	methods: {
		setAvailability() {
			const self = this;

			if (self.config.getAvailability) {
				// Check to see if overrides have been set
				if (self.availabilityData.availabilityOverride != "syncValue") {
					self.availability = self.camelize(self.availabilityData.availabilityOverride);
				}
				//check to see if manual instance
				else if (!self.importData.importId) {
					self.availability = "available";
				} else {
					if (Object.keys(self.apiEventData.availability).length) {
						if (
							self.type == "event" &&
							self.eventData.numberOfInstances > 1
						) {
							const eventAvailabilty = self.apiEventData.availability.stats;

							if (eventAvailabilty.instances_bookable == 0) {
								self.availability = "soldOut";
							} else {
								self.availability = "available";
							}
						} else {
							let instance = self.apiEventData.availability.instances.find(
								(o) => o.id == self.instanceData.instanceId,
							);
							const availabilityData = instance.availability;

							// named predicates of availability
							const isLow =
								availabilityData.available < availabilityData.capacity / 10;
							const isLimited =
								availabilityData.available < availabilityData.capacity / 20;
							const isSoldOut =
								availabilityData.available +
								availabilityData.specialAvailable +
								availabilityData.wheelchairAvailable <
								1;

							if (isSoldOut) {
								if (self.config.getLockedSeats) {
									const lockedSeatsUrl =
										"/api/custom-import/spektrix/locks/" +
										self.importData.importId;
									axios
										.get(lockedSeatsUrl, {
											//crossdomain: true,
										})
										.then((response) => {
											const lockData = response.data;
											if (
												lockData.dDeafBslUser ||
												lockData.generalAccessMember ||
												lockData.generalAccessMember ||
												lockData.wheelchairUser ||
												lockData.prologueLockCount > 0
											) {
												self.availability = "accessAvailability";
											} else {
												self.availability = "soldOut";
											}
										});
								}

								self.availability = "soldOut";
							} else if (isLow) {
								self.availability = "lowAvailability";
							} else if (isLimited) {
								self.availability = "sellingFast";
							} else {
								self.availability = "available";
							}
						}
					} else {
						//is likely to be in the past, so make the instance off sale
						self.availability = "available";
						//self.onSale = false;
					}
				}
			} else {
				self.availability = "available";
			}

			self.availabilityChecked = true;
		},
		camelize(str) {
			return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
				return index === 0 ? word.toLowerCase() : word.toUpperCase();
			}).replace(/\s+/g, '');
		},
		setEligibility() {
			var self = this;

			if (self.config.getEligibility) {
				const onSaleScheduleData = self.apiEventData.onSaleSchedules;

				var futureOnSaleSchedule;
				var mostRecentPastRow;

				if (onSaleScheduleData.length) {
					const map = new Map();
					onSaleScheduleData.forEach((item) => map.set(item.date, item));

					const mergedStatuses = Array.from(map.values());

					// Parse out the current on sale schedule value
					for (var i = 0; i < onSaleScheduleData.length; i++) {
						var onSaleScheduleDate = Date.parse(onSaleScheduleData[i].date);

						if (onSaleScheduleDate > self.currentDateTime) {
							if (i !== 0 && !self.currentOnSaleSchedule) {
								self.currentOnSaleSchedule = mergedStatuses[i - 1];
								futureOnSaleSchedule = mergedStatuses[i];
							}
						} else {
							self.currentOnSaleSchedule = null;
							mostRecentPastRow = mergedStatuses[i];
						}
					}

					if (!self.currentOnSaleSchedule && mostRecentPastRow) {
						self.currentOnSaleSchedule = mostRecentPastRow;
					}

					//if there is a future OSS - set up a cron to watch for when it changes
					//TODO: test this
					if (futureOnSaleSchedule && self.config.runCron) {
						let dateObject = new Date(futureOnSaleSchedule.date);
						let dateConstruct = `2 ${dateObject.getMinutes()} ${dateObject.getHours()} ${dateObject.getDate()} ${dateObject.getMonth() + 1} *`;

						const job = new CronJob(
							dateConstruct,
							async function () {
								self.eligibilityChecked = false;

								try {
									const response = await RequestManager.call(
										axios,
										"GET",
										"/datetime.php",
										null,
										{
											cache: {
												override: true,
											},
										},
									);

									self.currentDateTime = Date.parse(response.data);

									self.setEligibility();
								} catch (err) {
									// Handle error here
								}
							},
							null,
							true,
						);
					}

					if (self.currentOnSaleSchedule) {
						// Update values based on OSS value
						if (self.currentOnSaleSchedule.status === "public") {
							self.eligibility = "public";
						} else if (self.currentOnSaleSchedule.status === "members") {
							self.eligibility = "membersOnly";
						} else if (self.currentOnSaleSchedule.status === "comingSoon") {
							self.eligibility = "comingSoon";
						} else if (self.currentOnSaleSchedule.status === "notOnSale") {
							self.eligibility = "NotOnSale";
							console.error(
								"Non-visible On Sale Schedule as current in availability api response",
							);
						} else {
							console.error(
								"Unknown status schedule value in availability api response",
							);
						}
					} else {
						// No past rows logic here
						// Shouldn't ever get here, as no past rows events are hidden, so console error
						console.error(
							"reached no past rows on sale schedule logic in availability, when event should be hidden",
						);
					}
				}
			} else {
				self.eligibility = "public";
			}

			self.eligibilityChecked = true;
		},
	},
};
</script>
